/**
 * AnnualPlanView + SlotEditor — 48-slot calendar, override drawer,
 * rationale panel with context-nudge chips, and A3 PPTX export.
 *
 * Extracted from App.js during the P1 Phase-2 refactor. Zero
 * behavioural changes: same props, same test-ids, same DOM.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import {
  API, MONTH_ORDER, NUDGE_CHIPS, downloadFromPath, shortenProgramName,
} from "../lib/beaconUtils";
import { ThemeReplaceModal } from "./ThemeReplaceModal.jsx";
import { SlotEditor } from "./SlotEditor.jsx";

export function AnnualPlanView({ topics, industryCode, siteName, companyFooter,
                          appliedProfile, planId = "default", sites = [],
                          activeSiteId, onSelectSite, onJumpToGenerate }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // The plan is refetched whenever the applied Context Engine profile
  // changes — the LLM tailors the 48-week calendar to the operator's
  // industry, jurisdiction and free-text context.
  const planParams = useMemo(() => {
    const params = { plan_id: planId };
    if (appliedProfile?.industry) params.industry = appliedProfile.industry;
    if ((appliedProfile?.jurisdictions || []).length) {
      params.jurisdictions = appliedProfile.jurisdictions.join(",");
    }
    if (appliedProfile?.free_text_context) {
      params.free_text_context = appliedProfile.free_text_context;
    }
    if (appliedProfile?.audience) params.audience = appliedProfile.audience;
    return params;
  }, [appliedProfile, planId]);

  // Bulk pack builder — stock the Library with a full 4-format pack for
  // every calendar topic (existing titles skipped) as a background job.
  const [bulkJob, setBulkJob] = useState(null);
  const bulkPollRef = useRef(null);
  const pollBulk = useCallback(() => {
    clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/plan/bulk-packs/status`, {
          params: { plan_id: planId },
        });
        setBulkJob(r.data.status ? r.data : null);
        if (r.data.status !== "running") clearInterval(bulkPollRef.current);
      } catch { /* transient poll failure */ }
    }, 10000);
  }, [planId]);
  useEffect(() => {
    axios.get(`${API}/plan/bulk-packs/status`, { params: { plan_id: planId } })
      .then((r) => {
        if (r.data.status === "running") {
          setBulkJob(r.data);
          pollBulk();
        }
      })
      .catch(() => {});
    return () => clearInterval(bulkPollRef.current);
  }, [planId, pollBulk]);
  const startBulkPacks = async () => {
    if (!window.confirm(
      "Build a full comms pack (poster, toolbox, email, intranet) for every topic on the calendar and file them in the Library?\n\nTopics already in the Library are skipped. This runs in the background and can take 15–20 minutes for a full year. You can keep working — then run ✦ Review all in the Library when it's done.",
    )) return;
    try {
      const r = await axios.post(`${API}/plan/bulk-packs/build`, {
        plan_id: planId,
        site_name: siteName || null,
        company_footer: companyFooter || null,
        industry: appliedProfile?.industry || null,
        jurisdictions: appliedProfile?.jurisdictions || null,
        free_text_context: appliedProfile?.free_text_context || null,
        tone: appliedProfile?.tone || null,
        audience: appliedProfile?.audience || null,
        real_examples: appliedProfile?.real_examples || null,
        company_logo_b64: appliedProfile?.company_logo_b64 || null,
        brand_palette: appliedProfile?.brand_palette || null,
      });
      if (r.data.status === "running") {
        setBulkJob(r.data);
        pollBulk();
      } else if (r.data.status === "empty") {
        alert("Every calendar topic already has a pack in the Library — nothing to build.");
      }
    } catch (e) {
      alert(`Bulk build failed to start: ${e.response?.data?.detail || e.message}`);
    }
  };

  const reloadPlan = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/plan`, { params: planParams });
      setPlan(r.data);
    } catch (e) {
      setError(e.message);
    }
  }, [planParams]);

  // Undo — overrides are append-only, so removing the newest change
  // restores whatever the plan showed before it.
  const [lastChange, setLastChange] = useState(null);
  const [undoBusy, setUndoBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/plan/last-change`, { params: { plan_id: planId } })
      .then((r) => setLastChange(r.data.kind ? r.data : null))
      .catch(() => {});
  }, [planId, plan]);
  const undoLast = async () => {
    setUndoBusy(true);
    try {
      await axios.post(`${API}/plan/undo`, null, {
        params: { plan_id: planId },
      });
      await reloadPlan();
    } catch (e) {
      alert(`Undo failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUndoBusy(false);
    }
  };

  // "Why this plan?" side panel state + local free-text override so the
  // manager can nudge Claude without a full Context Engine round-trip.
  const [rationalePanelOpen, setRationalePanelOpen] = useState(false);
  const [freeTextDraft, setFreeTextDraft] = useState(
    appliedProfile?.free_text_context || "",
  );
  const [regenerating, setRegenerating] = useState(false);
  useEffect(() => {
    setFreeTextDraft(appliedProfile?.free_text_context || "");
  }, [appliedProfile?.free_text_context]);

  const regenerateWithContext = async () => {
    if (!appliedProfile?.industry) return;
    setRegenerating(true);
    try {
      const params = {
        industry: appliedProfile.industry,
        jurisdictions: (appliedProfile.jurisdictions || []).join(","),
        free_text_context: freeTextDraft || "",
        audience: appliedProfile.audience || "field",
        plan_id: planId,
      };
      const r = await axios.post(`${API}/plan/regenerate`, null, { params });
      setPlan(r.data);
      setRationalePanelOpen(false);
    } catch (e) {
      setError(`Regeneration failed: ${e.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/plan`, { params: planParams });
        if (!cancelled) { setPlan(r.data); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [planParams]);

  // Poll while the LLM is generating in the background. Static-pending
  // means the API returned the fallback while Claude is still working
  // on the industry-tailored plan — we retry every 10s until it lands
  // (up to 5 minutes / 30 attempts) so the user doesn't have to
  // manually refresh.
  useEffect(() => {
    if (!plan || plan.plan_source !== "static-pending") return undefined;
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/plan`, { params: planParams });
        if (cancelled) return;
        setPlan(r.data);
        if (r.data.plan_source !== "static-pending" || attempts >= 30) {
          return;
        }
      } catch (e) {
        if (attempts >= 30) return;
      }
      window.setTimeout(tick, 10000);
    };
    const t = window.setTimeout(tick, 10000);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [plan, planParams]);

  const slotsByMonth = useMemo(() => {
    const acc = {};
    if (plan) {
      for (const s of plan.slots) {
        (acc[s.month] = acc[s.month] || []).push(s);
      }
    }
    return acc;
  }, [plan]);

  // Each month's theme banner: the merged month_themes map (latest theme
  // override wins, else the dynamic/canonical base theme).
  const themeInfoForMonth = (month) =>
    plan?.month_themes?.[month] ||
    { theme_name: (slotsByMonth[month] || [])[0]?.base.theme_name || "", overridden: false };

  // Month whose "Change theme" modal is open (null = closed).
  const [themeModalMonth, setThemeModalMonth] = useState(null);

  // ---- Year toolbox pack (background job) ----
  const [yearJob, setYearJob] = useState(null);
  useEffect(() => {
    if (!yearJob || yearJob.status !== "building") return;
    let attempts = 0;
    const t = setInterval(async () => {
      attempts += 1;
      if (attempts > 500) {
        // ~25 min at 3s — give up rather than polling forever.
        clearInterval(t);
        setYearJob({ ...yearJob, status: "error",
          error: "Build timed out — please try again." });
        return;
      }
      try {
        const r = await axios.get(`${API}/plan/year-pack/${yearJob.job_id}`);
        setYearJob(r.data);
      } catch { clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [yearJob]);

  const startYearPack = async () => {
    if (!window.confirm(
      "Build the full-year toolbox pack? Every scheduled talk is generated (or reused from previous builds) and bundled into one branded PPTX with a facilitation guide — no sign-on sheets. This runs in the background and can take 10-20 minutes on a first build.",
    )) return;
    try {
      const r = await axios.post(`${API}/plan/year-pack`, {
        plan_id: planId,
        site_name: siteName || null,
        company_footer: companyFooter || null,
        industry: appliedProfile?.industry || null,
        jurisdictions: appliedProfile?.jurisdictions || null,
        free_text_context: appliedProfile?.free_text_context || null,
        tone: appliedProfile?.tone || null,
        real_examples: appliedProfile?.real_examples || null,
        company_logo_b64: appliedProfile?.company_logo_b64 || null,
        brand_palette: appliedProfile?.brand_palette || null,
      });
      setYearJob({ ...r.data, done: 0, total: 0 });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // ---- Cadence toggle (weekly / fortnightly) ----
  const [cadenceBusy, setCadenceBusy] = useState(false);
  const cadence = useMemo(() => {
    if (!plan?.slots?.length) return "weekly";
    const evenWeeks = plan.slots.filter((s) => s.week === 2 || s.week === 4);
    return evenWeeks.length && evenWeeks.every((s) => s.blank)
      ? "fortnightly" : "weekly";
  }, [plan]);

  const applyCadence = async (target) => {
    if (target === cadence) return;
    const msg = target === "fortnightly"
      ? "Switch to a fortnightly cadence? Each month keeps its top two topics (weeks 1 & 3) and weeks 2 & 4 become 'no comms'. You can switch back anytime."
      : "Reset to weekly cadence? All four weeks in every month are restored to the plan's base topics (change history is kept).";
    if (!window.confirm(msg)) return;
    setCadenceBusy(true);
    try {
      await axios.post(`${API}/plan/cadence`, {
        plan_id: planId,
        cadence: target,
        industry: appliedProfile?.industry || null,
        jurisdictions: appliedProfile?.jurisdictions || null,
        free_text_context: appliedProfile?.free_text_context || null,
      });
      await reloadPlan();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCadenceBusy(false);
    }
  };

  // ---- Drag & drop reorder (within a month) ----
  const [dragSlot, setDragSlot] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const sameMonth = (a, b) => a.split("-")[0] === b.split("-")[0];

  const swapSlots = async (fromSlotId, toSlotId) => {
    const a = plan?.slots.find((s) => s.slot === fromSlotId);
    const b = plan?.slots.find((s) => s.slot === toSlotId);
    if (!a || !b || a.slot === b.slot) return;
    try {
      await axios.post(`${API}/plan/override`, {
        plan_id: planId, slot: a.slot,
        override_topic_id: b.current.topic_id,
        reason: `drag reorder: swapped with ${b.slot}`,
      });
      await axios.post(`${API}/plan/override`, {
        plan_id: planId, slot: b.slot,
        override_topic_id: a.current.topic_id,
        reason: `drag reorder: swapped with ${a.slot}`,
      });
      await reloadPlan();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // Quick actions on the calendar tiles: '×' blanks a week (no comms),
  // '↩' restores a blanked week to its base topic. Both are append-only
  // overrides, so the history stays complete.
  const clearWeek = async (s) => {
    try {
      await axios.post(`${API}/plan/override`, {
        plan_id: planId,
        slot: s.slot, override_topic_id: "no-comms",
        reason: "removed from plan",
      });
      await reloadPlan();
    } catch (e) {
      alert(`Could not remove this week: ${e.message}`);
    }
  };

  const restoreWeek = async (s) => {
    try {
      await axios.post(`${API}/plan/override`, {
        plan_id: planId,
        slot: s.slot, override_topic_id: s.base.topic_id,
        reason: "restored week",
      });
      await reloadPlan();
    } catch (e) {
      alert(`Could not restore this week: ${e.message}`);
    }
  };

  const selectedSlotData = plan?.slots.find((s) => s.slot === selectedSlot);

  return (
    <section className="beacon-plan" data-testid="annual-plan-view">
      <div className="beacon-sticky-head" data-testid="plan-sticky-head">
      <div className="beacon-plan-header">
        <div>
          <div className="beacon-card-eyebrow">ANNUAL PLAN · 12-MONTH OVERLAY</div>
          <h2 className="beacon-card-title">Your campaign year — at a glance.</h2>
          <p className="beacon-card-lede">
            12 monthly themes, 4 weekly topics per theme. Click any week to
            swap in a different topic — or fork in an ad-hoc pack from your
            Library. Every change is append-only and visible in the slot
            history.
          </p>
        </div>
      </div>

      <div className="beacon-plan-toolbar" data-testid="plan-toolbar">
        <div className="beacon-plan-toolbar-group">
          {lastChange && (
            <button
              type="button"
              className="beacon-secondary-btn"
              data-testid="plan-undo"
              disabled={undoBusy}
              title={`Undo: ${lastChange.description}`}
              onClick={undoLast}
            >
              {undoBusy ? "Undoing…" : "↶ Undo"}
            </button>
          )}
          <div className="beacon-cadence-toggle" data-testid="cadence-toggle">
            <span className="beacon-tiny-label">CADENCE</span>
            <div className="beacon-cadence-buttons">
              <button
                type="button"
                className={`beacon-cadence-btn${cadence === "weekly" ? " is-active" : ""}`}
                data-testid="cadence-weekly"
                disabled={cadenceBusy}
                onClick={() => applyCadence("weekly")}
              >
                Weekly
              </button>
              <button
                type="button"
                className={`beacon-cadence-btn${cadence === "fortnightly" ? " is-active" : ""}`}
                data-testid="cadence-fortnightly"
                disabled={cadenceBusy}
                onClick={() => applyCadence("fortnightly")}
              >
                {cadenceBusy ? "Applying…" : "Fortnightly"}
              </button>
            </div>
          </div>
          {sites.length > 0 && (
            <div className="beacon-site-switcher" data-testid="plan-site-switcher">
              <span className="beacon-tiny-label">SITE</span>
              <select
                className="beacon-input beacon-site-switcher-select"
                data-testid="plan-site-select"
                value={activeSiteId || ""}
                onChange={(e) => onSelectSite(e.target.value)}
              >
                {sites.map((s) => (
                  <option key={s.site_id} value={s.site_id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="beacon-plan-toolbar-group">
          <div className="beacon-year-pack">
            {bulkJob?.status === "running" ? (
              <span className="beacon-year-pack-progress"
                data-testid="bulk-packs-progress">
                ⚡ Stocking Library… {bulkJob.done || 0}/{bulkJob.total || "?"}
              </span>
            ) : bulkJob?.status === "ready" ? (
              <span className="beacon-year-pack-progress"
                data-testid="bulk-packs-done"
                title={`${bulkJob.built} packs built${bulkJob.failed ? `, ${bulkJob.failed} failed` : ""}${bulkJob.skipped ? `, ${bulkJob.skipped} already existed` : ""}. Run ✦ Review all in the Library to score them.`}>
                ✓ Library stocked ({bulkJob.built})
              </span>
            ) : (
              <button type="button" className="beacon-secondary-btn"
                data-testid="bulk-packs-build"
                onClick={startBulkPacks}
                title="Generate the full 4-format comms pack for every calendar topic and file them in the Library — review the whole year with Luma in one session">
                ⚡ Build all packs
              </button>
            )}
            {bulkJob?.status === "error" && (
              <span className="beacon-tab-error">{bulkJob.error}</span>
            )}
          </div>
          <div className="beacon-year-pack">
            {!yearJob || yearJob.status === "error" ? (
              <button type="button" className="beacon-secondary-btn"
                data-testid="year-pack-button"
                onClick={startYearPack}
                title="One PPTX: facilitation guide + every scheduled toolbox talk, branded, no sign-on sheets">
                ⧉ Year toolbox pack
              </button>
            ) : yearJob.status === "ready" ? (
              <button type="button" className="beacon-cta beacon-cta-compact"
                data-testid="year-pack-download"
                onClick={() => downloadFromPath(
                  `/api/beacon/plan/year-pack/${yearJob.job_id}/download`,
                  "beacon-year-toolbox.pptx",
                )}>
                ↓ Year pack ready
              </button>
            ) : (
              <span className="beacon-year-pack-progress"
                data-testid="year-pack-progress">
                Building… {yearJob.done || 0}/{yearJob.total || "?"}
              </span>
            )}
            {yearJob?.status === "error" && (
              <span className="beacon-tab-error">{yearJob.error}</span>
            )}
          </div>
          <button
            type="button"
            className="beacon-secondary-btn"
            data-testid="export-plan-button"
            onClick={() => {
              const params = new URLSearchParams();
              params.set("plan_id", planId);
              params.set("site_name", siteName || "Beacon");
              if (companyFooter) params.set("company_footer", companyFooter);
              // Pipe the applied profile through so the exported A3 uses
              // the same dynamic base plan the UI just rendered.
              if (planParams.industry) params.set("industry", planParams.industry);
              if (planParams.jurisdictions) params.set("jurisdictions", planParams.jurisdictions);
              if (planParams.free_text_context) params.set("free_text_context", planParams.free_text_context);
              if (planParams.audience) params.set("audience", planParams.audience);
              downloadFromPath(
                `/api/beacon/plan/export.pptx?${params.toString()}`,
                `beacon-annual-plan-${(siteName || "beacon")
                  .toLowerCase()
                  .replace(/[^a-z0-9-]/g, "")}.pptx`,
              );
            }}
          >
            ↓ Export plan (A3 PPTX)
          </button>
        </div>
      </div>

      {plan && (plan.plan_source === "llm" || plan.plan_source === "cache") && plan.generated_for && (
        <button
          type="button"
          className="beacon-plan-generated-for beacon-plan-generated-for-btn"
          data-testid="plan-generated-for"
          onClick={() => setRationalePanelOpen(true)}
          title="Why this plan? Tweak the context and regenerate."
        >
          <span className="beacon-plan-generated-for-label">Tailored for</span>
          <span className="beacon-plan-generated-for-body">
            {plan.generated_for.industry}
            {(plan.generated_for.jurisdictions || []).length > 0 && (
              <> · {(plan.generated_for.jurisdictions || []).join(", ")}</>
            )}
          </span>
          <span className="beacon-plan-generated-for-cta">Why this plan? →</span>
        </button>
      )}
      {plan && plan.plan_source === "static-pending" && (
        <div
          className="beacon-plan-generated-for beacon-plan-generated-for-pending"
          data-testid="plan-generated-for-pending"
        >
          <span className="beacon-plan-generated-for-label">
            <span className="beacon-spinner beacon-spinner-dark beacon-spinner-inline" />
            Generating
          </span>
          <span className="beacon-plan-generated-for-body">
            Beacon is tailoring the 48-week plan for{" "}
            <strong>{plan.generated_for?.industry || appliedProfile?.industry}</strong>
            {(plan.generated_for?.jurisdictions || []).length > 0 && (
              <> ({(plan.generated_for.jurisdictions || []).join(", ")})</>
            )}
            . Showing the canonical library in the meantime — this page
            will refresh automatically when the industry plan is ready
            (usually 60–120 seconds).
          </span>
        </div>
      )}
      {plan && plan.plan_source === "static" && appliedProfile?.industry && (
        <div
          className="beacon-plan-generated-for beacon-plan-generated-for-static"
          data-testid="plan-generated-for-static"
        >
          <span className="beacon-plan-generated-for-label">Static plan</span>
          <span className="beacon-plan-generated-for-body">
            The dynamic plan for {appliedProfile.industry} is queued for
            generation — showing the canonical 48-week library in the meantime.
          </span>
        </div>
      )}
      </div>

      {rationalePanelOpen && (
        <div
          className="beacon-rationale-panel-backdrop"
          data-testid="rationale-panel-backdrop"
          onClick={() => setRationalePanelOpen(false)}
        >
          <aside
            className="beacon-rationale-panel"
            data-testid="rationale-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="beacon-rationale-panel-head">
              <div className="beacon-rationale-panel-eyebrow">
                Why this plan?
              </div>
              <button
                type="button"
                className="beacon-rationale-panel-close"
                data-testid="rationale-panel-close"
                onClick={() => setRationalePanelOpen(false)}
                aria-label="Close"
              >×</button>
            </div>

            <div className="beacon-rationale-panel-section">
              <div className="beacon-rationale-panel-label">Tailored for</div>
              <div className="beacon-rationale-panel-profile">
                <strong>{plan?.generated_for?.industry || appliedProfile?.industry}</strong>
                {(plan?.generated_for?.jurisdictions || []).length > 0 && (
                  <span> · {(plan.generated_for.jurisdictions || []).join(", ")}</span>
                )}
                {plan?.plan_source === "cache" && (
                  <span className="beacon-rationale-panel-source">Cached</span>
                )}
                {plan?.plan_source === "llm" && (
                  <span className="beacon-rationale-panel-source beacon-rationale-panel-source-fresh">
                    Fresh
                  </span>
                )}
              </div>
            </div>

            <div className="beacon-rationale-panel-section">
              <div className="beacon-rationale-panel-label">
                Claude&apos;s rationale
              </div>
              <p
                className="beacon-rationale-panel-body"
                data-testid="rationale-panel-body"
              >
                {plan?.plan_rationale || "No rationale on file for this plan."}
              </p>
            </div>

            <div className="beacon-rationale-panel-section">
              <div className="beacon-rationale-panel-label">
                Nudge the context
              </div>
              <p className="beacon-rationale-panel-hint">
                Add site-specific notes to reshape the plan — for example
                &ldquo;day surgery only, no inpatient&rdquo; or &ldquo;night
                shift-heavy fleet&rdquo;. Regenerate to have Claude re-order
                the year.
              </p>
              <div className="beacon-rationale-panel-chips" data-testid="rationale-panel-chips">
                {NUDGE_CHIPS.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    className="beacon-rationale-panel-chip"
                    data-testid={`nudge-chip-${chip.label
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => {
                      setFreeTextDraft((prev) => {
                        const base = (prev || "").trim();
                        const separator = base && !base.endsWith(".") ? ". " : (base ? " " : "");
                        const next = `${base}${separator}${chip.phrase}`.slice(0, 500);
                        return next;
                      });
                    }}
                    title={chip.phrase}
                  >
                    + {chip.label}
                  </button>
                ))}
              </div>
              <textarea
                className="beacon-rationale-panel-textarea"
                data-testid="rationale-panel-textarea"
                value={freeTextDraft}
                onChange={(e) => setFreeTextDraft(e.target.value)}
                placeholder="Optional — site-specific context (max 500 chars)"
                maxLength={500}
                rows={4}
              />
              <div className="beacon-rationale-panel-count">
                {freeTextDraft.length}/500
              </div>
            </div>

            <div className="beacon-rationale-panel-actions">
              <button
                type="button"
                className="beacon-secondary-btn"
                data-testid="rationale-panel-cancel"
                onClick={() => setRationalePanelOpen(false)}
                disabled={regenerating}
              >Cancel</button>
              <button
                type="button"
                className="beacon-primary-btn"
                data-testid="rationale-panel-regenerate"
                onClick={regenerateWithContext}
                disabled={regenerating || !appliedProfile?.industry}
              >
                {regenerating ? (
                  <><span className="beacon-spinner beacon-spinner-inline" />Regenerating…</>
                ) : (
                  <>Regenerate plan →</>
                )}
              </button>
            </div>
          </aside>
        </div>
      )}


      {loading && (
        <div className="beacon-tab-loading" data-testid="plan-loading">
          <span className="beacon-spinner beacon-spinner-dark" />Loading plan…
        </div>
      )}
      {error && (<div className="beacon-tab-error" data-testid="plan-error">{error}</div>)}

      {plan && (
        <div className="beacon-calendar" data-testid="annual-calendar">
          {MONTH_ORDER.map((month) => {
            const slots = slotsByMonth[month] || [];
            const themeInfo = themeInfoForMonth(month);
            return (
              <div key={month} className="beacon-calendar-month"
                data-testid={`month-${month.toLowerCase()}`}>
                <div className="beacon-calendar-month-name">{month}</div>
                <div className="beacon-calendar-theme-row">
                  <div className="beacon-calendar-theme-banner"
                    data-testid={`theme-${month.toLowerCase()}`}>
                    {themeInfo.theme_name}
                    {themeInfo.overridden && (
                      <span
                        className="beacon-theme-edited-chip"
                        data-testid={`theme-edited-${month.toLowerCase()}`}
                        title={`Theme replaced (${themeInfo.mode})`}
                      >
                        {themeInfo.mode === "custom" ? "custom" : "edited"}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="beacon-theme-change-btn"
                    data-testid={`change-theme-${month.toLowerCase()}`}
                    onClick={() => setThemeModalMonth(month)}
                    title="Change this month's theme"
                  >⇄</button>
                </div>
                <div className="beacon-calendar-weeks">
                  {slots
                    .filter((s) => cadence !== "fortnightly" || !s.blank)
                    .map((s) => {
                    const relevantForIndustry =
                      !industryCode ||
                      (s.current.industry_relevance || []).includes(industryCode);
                    return (
                      <button
                        key={s.slot}
                        type="button"
                        data-testid={`slot-${s.slot}`}
                        draggable={!s.blank}
                        onDragStart={(e) => {
                          setDragSlot(s.slot);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", s.slot);
                        }}
                        onDragEnd={() => {
                          setDragSlot(null);
                          setDropTarget(null);
                        }}
                        onDragOver={(e) => {
                          if (dragSlot && dragSlot !== s.slot
                              && sameMonth(dragSlot, s.slot)) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            if (dropTarget !== s.slot) setDropTarget(s.slot);
                          }
                        }}
                        onDragLeave={() => {
                          if (dropTarget === s.slot) setDropTarget(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from =
                            e.dataTransfer.getData("text/plain") || dragSlot;
                          if (from && from !== s.slot
                              && sameMonth(from, s.slot)) {
                            swapSlots(from, s.slot);
                          }
                          setDragSlot(null);
                          setDropTarget(null);
                        }}
                        className={`beacon-calendar-week${
                          selectedSlot === s.slot ? " is-selected" : ""
                        }${s.overridden ? " is-overridden" : ""}${
                          s.blank ? " is-blank" : ""
                        }${
                          s.current.mental_health_related ? " is-mental-health" : ""
                        }${!relevantForIndustry && !s.blank ? " is-off-industry" : ""}${
                          dragSlot === s.slot ? " is-dragging" : ""
                        }${dropTarget === s.slot ? " is-drop-target" : ""}`}
                        onClick={() => setSelectedSlot(s.slot)}
                      >
                        <span className="beacon-calendar-week-num">W{s.week}</span>
                        <span className="beacon-calendar-week-name">
                          {s.current.topic_name}
                        </span>
                        {!s.blank ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="beacon-week-clear"
                            data-testid={`slot-clear-${s.slot}`}
                            title="Remove this week from the plan (no comms this week)"
                            aria-label={`Remove ${s.current.topic_name} from the plan`}
                            onClick={(e) => { e.stopPropagation(); clearWeek(s); }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.stopPropagation();
                                e.preventDefault();
                                clearWeek(s);
                              }
                            }}
                          >×</span>
                        ) : (
                          <span
                            role="button"
                            tabIndex={0}
                            className="beacon-week-clear beacon-week-restore"
                            data-testid={`slot-restore-${s.slot}`}
                            title={`Restore this week (${s.base.topic_name})`}
                            aria-label={`Restore ${s.base.topic_name}`}
                            onClick={(e) => { e.stopPropagation(); restoreWeek(s); }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.stopPropagation();
                                e.preventDefault();
                                restoreWeek(s);
                              }
                            }}
                          >↩</span>
                        )}
                        {s.blank && (
                          <span className="beacon-calendar-blank-tag"
                            data-testid={`blank-tag-${s.slot}`}>no comms</span>
                        )}
                        {s.overridden && !s.blank && (
                          <span className="beacon-slot-dot is-override"
                            title="Changed from the base plan"
                            data-testid={`override-tag-${s.slot}`} />
                        )}
                        {s.forked_from_program && (
                          <span
                            className="beacon-calendar-program-chip"
                            data-testid={`program-chip-${s.slot}`}
                            title={`Forked from: ${s.forked_from_program.name}`}
                          >
                            ↗ {shortenProgramName(s.forked_from_program.name)}
                          </span>
                        )}
                        {!relevantForIndustry && !s.blank && (
                          <span
                            className="beacon-slot-dot is-off-industry"
                            data-testid={`off-industry-${s.slot}`}
                            title="Not typical for your industry"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {themeModalMonth && (
        <ThemeReplaceModal
          month={themeModalMonth}
          themeInfo={themeInfoForMonth(themeModalMonth)}
          appliedProfile={appliedProfile}
          planId={planId}
          onClose={() => setThemeModalMonth(null)}
          onApplied={reloadPlan}
        />
      )}

      {selectedSlotData && (
        <SlotEditor
          key={selectedSlotData.slot}
          slot={selectedSlotData}
          topics={topics}
          appliedProfile={appliedProfile}
          planId={planId}
          monthTheme={themeInfoForMonth(selectedSlotData.month)?.theme_name}
          onClose={() => setSelectedSlot(null)}
          onReload={reloadPlan}
          onJumpToGenerate={onJumpToGenerate}
        />
      )}
    </section>
  );
}

